/** Seeded, deterministic random number generation. */

import type { RNG } from './types';

/** Mulberry32 — small, fast, good enough statistically for level generation. */
class Mulberry32 implements RNG {
  private state: number;
  readonly seed: number;

  constructor(seed: number) {
    this.seed = seed >>> 0;
    this.state = this.seed;
  }

  next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  int(min: number, max: number): number {
    return Math.floor(min + this.next() * (max - min + 1));
  }

  chance(p: number): boolean {
    return this.next() < p;
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('rng.pick: empty array');
    return items[Math.floor(this.next() * items.length)]!;
  }

  shuffle<T>(items: T[]): T[] {
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(this.next() * (i + 1));
      const tmp = items[i]!;
      items[i] = items[j]!;
      items[j] = tmp;
    }
    return items;
  }

  fork(salt = 0): RNG {
    // Derive a new independent stream. Consumes one value from this stream so
    // repeated forks with the same salt still differ.
    const s = (Math.imul(this.state ^ (salt + 0x9e3779b9), 0x85ebca6b) ^ (this.next() * 0xffffffff)) >>> 0;
    return new Mulberry32(s);
  }
}

/** Create a deterministic RNG stream. */
export function makeRNG(seed: number): RNG {
  return new Mulberry32(seed >>> 0);
}

/** Hash a string to a 32-bit seed (FNV-1a). Useful for named seeds. */
export function hashSeed(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** A fresh, unpredictable seed for a new run. */
export function randomSeed(): number {
  return (Math.random() * 0xffffffff) >>> 0;
}

/** Pick from a weighted table. Weights need not sum to 1. */
export function weightedPick<T>(rng: RNG, table: ReadonlyArray<{ kind: T; weight: number }>): T {
  let total = 0;
  for (const e of table) total += e.weight;
  if (total <= 0) throw new Error('weightedPick: non-positive total weight');
  let r = rng.next() * total;
  for (const e of table) {
    r -= e.weight;
    if (r <= 0) return e.kind;
  }
  return table[table.length - 1]!.kind;
}
